import { handleApiError, ServiceError } from '../../../services/utils/apiErrorUtils';
import { RateLimitError } from '../types';

export interface AuthError {
  message: string;
  type: 'validation' | 'authentication' | 'authorization' | 'rate_limit' | 'network' | 'server' | 'unknown';
  statusCode?: number;
  rateLimitInfo?: RateLimitError;
  field?: string; // For field-specific validation errors
}

/**
 * Handles authentication-specific errors with enhanced context
 */
export function handleAuthError(error: any, defaultMessage: string = 'An error occurred'): AuthError {
  const serviceError = handleApiError(error);
  
  // Initialize auth error
  const authError: AuthError = {
    message: defaultMessage,
    type: 'unknown',
    statusCode: serviceError.statusCode,
  };

  // Extract rate limiting information if present
  if (error?.rateLimitInfo) {
    authError.rateLimitInfo = error.rateLimitInfo;
    authError.type = 'rate_limit';
    authError.message = `Too many requests. Please try again in ${error.rateLimitInfo.retry_after} seconds.`;
    return authError;
  }

  // Handle different status codes
  if (serviceError.statusCode) {
    switch (serviceError.statusCode) {
      case 400:
        authError.type = 'validation';
        authError.message = extractValidationMessage(error) || serviceError.message || 'Invalid request data';
        break;
      case 401:
        authError.type = 'authentication';
        authError.message = serviceError.message || 'Invalid credentials';
        break;
      case 403:
        authError.type = 'authorization';
        authError.message = serviceError.message || 'Access denied';
        break;
      case 429:
        authError.type = 'rate_limit';
        authError.message = serviceError.message || 'Too many requests';
        break;
      case 500:
      case 502:
      case 503:
        authError.type = 'server';
        authError.message = 'Server error. Please try again later.';
        break;
      default:
        if (serviceError.statusCode >= 400 && serviceError.statusCode < 500) {
          authError.type = 'validation';
        } else if (serviceError.statusCode >= 500) {
          authError.type = 'server';
        }
        authError.message = serviceError.message || defaultMessage;
    }
  } else {
    // Network or other errors
    authError.type = 'network';
    authError.message = serviceError.message || 'Network error. Please check your connection.';
  }

  return authError;
}

/**
 * Extract validation message from error response
 */
function extractValidationMessage(error: any): string | null {
  const originalError = error?.response?.data || error?.originalError?.response?.data || error;
  
  if (!originalError || typeof originalError !== 'object') {
    return null;
  }

  // Handle Django REST framework error formats
  if (originalError.error) {
    return originalError.error;
  }
  
  if (originalError.detail) {
    return originalError.detail;
  }
  
  if (originalError.message) {
    return originalError.message;
  }

  // Handle field-specific errors
  if (originalError.non_field_errors && Array.isArray(originalError.non_field_errors)) {
    return originalError.non_field_errors[0];
  }

  // Handle field validation errors
  const fieldErrors = Object.keys(originalError).filter(key => 
    Array.isArray(originalError[key]) && key !== 'non_field_errors'
  );
  
  if (fieldErrors.length > 0) {
    const field = fieldErrors[0];
    const message = originalError[field][0];
    return `${field}: ${message}`;
  }

  return null;
}

/**
 * Specific error handlers for common auth scenarios
 */
export const authErrorHandlers = {
  /**
   * Handle login errors
   */
  login: (error: any): AuthError => {
    const authError = handleAuthError(error, 'Invalid email or password');
    
    // Customize message for login-specific scenarios
    if (authError.type === 'validation') {
      // Check for specific login error patterns
      const message = authError.message.toLowerCase();
      if (message.includes('invalid') || message.includes('incorrect')) {
        authError.message = 'Invalid email or password. Please try again.';
      } else if (message.includes('inactive') || message.includes('disabled')) {
        authError.message = 'Your account is inactive. Please contact support.';
      } else if (message.includes('blocked') || message.includes('locked')) {
        authError.message = 'Your account has been temporarily locked due to too many failed attempts.';
      }
    }
    
    return authError;
  },

  /**
   * Handle registration errors
   */
  register: (error: any): AuthError => {
    const authError = handleAuthError(error, 'Registration failed');
    
    if (authError.type === 'validation') {
      const message = authError.message.toLowerCase();
      if (message.includes('email') && message.includes('exists')) {
        authError.message = 'An account with this email already exists.';
        authError.field = 'email';
      } else if (message.includes('password')) {
        authError.message = 'Password does not meet security requirements.';
        authError.field = 'password';
      }
    }
    
    return authError;
  },

  /**
   * Handle password reset errors
   */
  passwordReset: (error: any): AuthError => {
    const authError = handleAuthError(error, 'Failed to send reset email');
    
    if (authError.type === 'validation') {
      const message = authError.message.toLowerCase();
      if (message.includes('email')) {
        if (message.includes('not found') || message.includes('does not exist')) {
          authError.message = 'No account found with that email address.';
        } else if (message.includes('invalid')) {
          authError.message = 'Please enter a valid email address.';
        }
      }
    }
    
    return authError;
  },

  /**
   * Handle token verification errors
   */
  tokenVerification: (error: any): AuthError => {
    const authError = handleAuthError(error, 'Invalid or expired token');
    
    if (authError.type === 'validation') {
      const message = authError.message.toLowerCase();
      if (message.includes('expired')) {
        authError.message = 'This reset link has expired. Please request a new one.';
      } else if (message.includes('invalid') || message.includes('not found')) {
        authError.message = 'This reset link is invalid. Please request a new one.';
      } else if (message.includes('used') || message.includes('already')) {
        authError.message = 'This reset link has already been used. Please request a new one.';
      }
    }
    
    return authError;
  },

  /**
   * Handle password change/update errors
   */
  passwordUpdate: (error: any): AuthError => {
    const authError = handleAuthError(error, 'Failed to update password');
    
    if (authError.type === 'validation') {
      const message = authError.message.toLowerCase();
      if (message.includes('weak') || message.includes('strength')) {
        authError.message = 'Password is too weak. Please choose a stronger password.';
        authError.field = 'password';
      } else if (message.includes('match') || message.includes('same')) {
        authError.message = 'New password cannot be the same as your current password.';
        authError.field = 'password';
      } else if (message.includes('current') || message.includes('old')) {
        authError.message = 'Current password is incorrect.';
        authError.field = 'current_password';
      }
    }
    
    return authError;
  },

  /**
   * Handle 2FA errors
   */
  twoFactor: (error: any): AuthError => {
    const authError = handleAuthError(error, '2FA verification failed');
    
    if (authError.type === 'validation') {
      const message = authError.message.toLowerCase();
      if (message.includes('invalid') || message.includes('incorrect')) {
        authError.message = 'Invalid verification code. Please try again.';
        authError.field = 'token';
      } else if (message.includes('expired')) {
        authError.message = 'Verification code has expired. Please generate a new one.';
        authError.field = 'token';
      }
    }
    
    return authError;
  }
};

/**
 * Get user-friendly error message based on error type
 */
export function getErrorMessage(authError: AuthError): string {
  return authError.message;
}

/**
 * Check if error is related to rate limiting
 */
export function isRateLimitError(authError: AuthError): boolean {
  return authError.type === 'rate_limit' || !!authError.rateLimitInfo;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(authError: AuthError): boolean {
  return authError.type === 'validation';
}

/**
 * Check if error is a network error
 */
export function isNetworkError(authError: AuthError): boolean {
  return authError.type === 'network';
}

/**
 * Check if error is a server error
 */
export function isServerError(authError: AuthError): boolean {
  return authError.type === 'server';
}